import { endOfDay, startOfDay } from "date-fns";
import {
	defined,
	TDateRangeType,
} from "../../../component/common/ZFDateRangePicker/defaultStaticRanges";
import {
	TFilterDropDownConfig,
	TFilterDropDownData,
} from "../../../fixtures/data-fixtures/ComponentConfigFixtures";
import { Pick_A } from "../../../fixtures/utilityFixtures";
import { defaultPageProperties, legendProperties } from "../fixtures";
import {
	IColumnMetaData,
	IMergedColumnMetaData,
	IProgressColumnMetaData,
	TFilterAPIConfig,
	TFilterData,
	TOrderModuleLayout,
	TOrderTableData,
	TOrderTabTypes,
} from "../orderDataProvider";
import {
	ColumnType,
	IDefaultColumnType,
	IMergedColumnType,
	IOrderItem,
	IOrderListingConfig,
	IOrderListingHeader,
	IProgressBarColumnType,
	IProgressBarObject,
	RowColumnType,
} from "./OrderListingTable/OrderListingTable.d";
import { TOrderFilterConfig } from "./orderStateReducer";

export const tabConfigTransformation = (layout: TOrderModuleLayout) => {
	return layout.tabTypes.map((tab) => {
		return {
			title: tab.title,
			tabType: tab.tabType,
			enableDetailView: tab.orderDetailViewPresent,
			table: {
				config: {
					columnMetaData: tab.columnMetaData as Array<IColumnMetaData>,
					orderListingConfig: {
						orderItems: [],
						totalRecords: 0,
					} as IOrderListingConfig,
					headers: getTableHeaders(tab.columnMetaData as any),
				},
			},
			filters: {
				config: filterConfigTransformation(tab.tabFilters),
				data: {
					selected: [] as TFilterDropDownData[],
					rest: [] as TFilterDropDownData[],
					basedOnSelection: [] as TFilterDropDownData[],
					all: [] as TFilterDropDownData[],
				},
			},
			pageProperties: { ...defaultPageProperties },
			dateProperties: getDateProps(tab.tabType, tab.tabFilters),
		};
	});
};

const getDateProps = (
	tabType: TOrderTabTypes,
	tabFilters: TFilterAPIConfig[]
) => {
	const x = tabFilters.find((f) => {
		return f.filterType === "DATE_FILTERS";
	});
	console.log("tabType", tabType);

	if (!x) {
		return {
			config: [] as TDateRangeType[],
			dateRange: {
				startDate: startOfDay(new Date()),
				endDate: endOfDay(new Date()),
			},
			hidden: true,
		};
	}
	switch (tabType) {
		case "not_started":
		case "in_progress":
			return {
				config: [
					"TODAY",
					"YESTERDAY",
					"LAST_7_DAYS",
				] as TDateRangeType[],
				dateRange: {
					startDate: defined.startOfToday,
					endDate: defined.endOfToday,
				},
			};
		case "completed":
			return {
				config: [
					"TODAY",
					"YESTERDAY",
					"LAST_7_DAYS",
					"LAST_30_DAYS",
				] as TDateRangeType[],
				dateRange: {
					startDate: defined.startOfLast30Days,
					endDate: defined.startOfToday,
				},
			};
		default:
			console.error("Invalid state");
			return {
				config: [] as TDateRangeType[],
				dateRange: {
					startDate: startOfDay(new Date()),
					endDate: endOfDay(new Date()),
				},
				hidden: true,
			};
	}
};

export type TOrderModuleTabs = ReturnType<typeof tabConfigTransformation>;

export type TOrderTableTab = Pick_A<TOrderModuleTabs, 0>;

const progressColumnClasses = ["order-table-flex-3"];

export const getTableHeaders = (
	columnMetaData: Array<IColumnMetaData> = []
): Array<IOrderListingHeader> => {
	const columns: Array<IOrderListingHeader> = [];
	columnMetaData
		.sort((a, b) => a.columnIndex - b.columnIndex)
		.forEach((column) => {
			let value;
			if (column.type === "MERGED") {
				value = (column.columnJsonKeys as Array<IMergedColumnMetaData>)
					.sort((a, b) => a.columnIndex - b.columnIndex)
					.map((_) => _.columnDisplayName)
					.join("/");
			} else {
				value = column.columnDisplayName;
			}
			columns.push({
				classes:
					column.type === "PROGRESS" ? progressColumnClasses : [],
				value,
			});
		});
	return columns;
};

export const getTableConfig = (
	columnConfig: Array<IColumnMetaData>,
	data: TOrderTableData
): IOrderListingConfig => {
	const totalRecords = data.total;
	const orderItems: Array<IOrderItem> = data.rows.map((row) => {
		const fields = columnConfig.map((column) => {
			let field = {
				classes: [] as Array<string>,
				key: column.columnJsonKey as string,
			} as RowColumnType;
			let valueArray;
			switch (column.type as ColumnType) {
				case "MERGED":
					valueArray = (column.columnJsonKeys as Array<IMergedColumnMetaData>)
						.sort((a, b) => a.columnIndex - b.columnIndex)
						.map((col, index: number) => {
							const value =
								index === 0
									? row[col.columnJsonKey]
									: `${col.columnDisplayName}: ${
											row[col.columnJsonKey]
									  }`;
							return { key: col.columnJsonKey, value };
						}) as Array<IDefaultColumnType>;

					field = {
						...field,
						valueArray,
						columnType: column.type,
					} as IMergedColumnType;
					break;
				case "PROGRESS":
					let total = 0;

					valueArray = (column.columnJsonKeys as Array<IProgressColumnMetaData>)
						.sort((a, b) => a.columnIndex - b.columnIndex)
						.map((json) => {
							const legendProp = legendProperties.find(
								(_) => _.key === json.columnJsonKey
							);
							const primaryValue =
								row["progress"]?.[json.columnJsonKey] || 0;
							total += primaryValue;
							return {
								key: json.columnJsonKey,
								primaryValue,
								legendColor: legendProp?.iconColor,
							};
						}) as Array<IProgressBarObject>;

					let percentAdded = 0;

					valueArray.forEach((v, index: number) => {
						let barPercentage =
							total !== 0
								? Math.round((v.primaryValue / total) * 100)
								: 0;
						if (index === valueArray.length - 1) {
							barPercentage = 100 - percentAdded;
						}
						percentAdded += barPercentage;
						v["barPercentage"] = barPercentage;
					});

					field = {
						...field,
						valueArray,
						columnType: column.type,
						classes: progressColumnClasses,
					} as IProgressBarColumnType;
					break;
				default:
					field = {
						...field,
						value: row[column.columnJsonKey as string],
						columnType: column.type,
					} as IDefaultColumnType;
			}

			return field;
		});

		const rowClasses: Array<string> = [];
		return { fields, rowClasses, orderShippableId: row.orderShippableId };
	});

	return { totalRecords, orderItems };
};

const filterConfigTransformation = (
	tabFilters: TFilterAPIConfig[]
): TFilterDropDownConfig[] => {
	return tabFilters
		.filter((t) => t.filterType === "BCTX_FILTER")
		.map((f) => {
			return {
				allowSearchFilter: true,
				default: true,
				display_name: f.displayName || "",
				dynamic_filter: true,
				hidden: false,
				id: `${Math.random()}_key`,
				key: f.key,
				searchPlaceholderText: `Search ${capitalize(f.displayName)}`,
				singleSelection: false,
			};
		});
};

const capitalize = (word?: string) => {
	if (word) {
		return word[0].toUpperCase() + word.slice(1).toLowerCase();
	}
	return "";
};

export const filterDataTransformation = (
	filterConfig: TOrderFilterConfig[],
	filterData: TFilterData
): TFilterDropDownData[] => {
	return filterConfig.flatMap((f) => {
		const c = (filterData[f.key] as string[]) || [];
		return c.map((d: string) => {
			return { dimension: f.key, value: d };
		});
	});
};
